import Basket from './Basket';
import Config from '../Config';
import Helper from '../Classes/Helper';
import Layout from './Layout';
import Merger from '../Classes/Merger';
import Registry from './Registry';
import {get, writable} from 'svelte/store';

let data = {};
const product = writable(data);

function selectedIdValues() {
	let selected = [];
	if (Array.isArray(data.steps))
		for (let step of data.steps)
			for (let property of step.properties) {
				const type = property.property_type;
				const referencing = !['options'].includes(type);
				const populated = Helper.is(property.selected);
				if (referencing && populated) {
					const value = property.selected.value;
					selected.push(value);
				}
			}

	return selected;
}

function setVisible() {
	let changed = false;
	for (let step of data.steps)
		for (let property of step.properties) {
			const visible = product.visibleProperty(property);
			if (property.visible != visible) {
				property.visible = visible;
				changed = true;
			}
		}

	return changed;
}

function setState() {
	let changed = false;
	const exclude = [];
	for (let step of data.steps) {
		let valid = true;
		const selected = {one: false, all: false};
		for (let property of step.properties)
			if (valid && property.required) {
				if (property.visible && !exclude.includes(property.property_type)) {
					if (!selected.one) {
						selected.one = Helper.is(property.selected);
						selected.all = property.selected.valid;
						valid = selected.one && selected.all;
					}

					if (selected.one && !property.selected.valid) {
						selected.all = false;
						valid = false;
					}
				}
			}

		let state;
		const skipped = step.order <= data.step.order;
		const force = !data.step.order && data.config.force_step_state;
		if (valid) state = (force || skipped) ? 'success' : 'empty';
		else if (selected.one) state = selected.all ? 'success' : 'error';
		else state = (force || skipped) ? 'error' : 'empty';
		if (step.state != state) {
			step.state = state;
			changed = true;
		}
	}

	return changed;
}

function setVariation() {
	let changed = false;
	if (!Helper.is(data.variation)) {
		data.variation = data.variations[0];
		changed = true
	}

	for (let variation of data.variations) {
		let valid = true;

		options:
			for (let option of variation.options)
				steps:
					for (let step of data.steps)
						for (let property of step.properties)
							if (property.option_id === option.option_id) {
								const optioned = !!property.selected.option_value_id;
								const option_key = optioned ? 'option_value_id' : 'value';
								const match = property.selected[option_key];
								valid = match === option.value_id;
								option.valid = valid;
								if (!valid) break options;
								else break steps;
							}

		if (valid) {
			changed = data.variation !== variation;
			if (changed) data.variation = variation;
			break;
		}
	}

	return changed;
}

function setPrice() {
	let extra = 0;
	for (let step of data.steps)
		for (let property of step.properties)
			if (property.visible && property.selected && property.selected.price)
				extra += property.selected.price;

	const base = data.variation.price;
	const changed = (data.price.base !== base) || (data.price.extra !== extra);
	if (changed) {
		data.price.base = base;
		data.price.extra = extra;
		data.price.total = data.price.base + data.price.extra;
		data.price.total = Math.round((data.price.total + Number.EPSILON) * 100) / 100;
	}

	return changed;
}

function validGroup(group) {
	return !group.list_seller || get(Registry).seller;
}

function validGroups(groups) {
	let valid = 0;
	const populated = Helper.is(groups) && groups.length;
	if (populated) for (let group of groups)
		if (validGroup(group)) valid++;

	return valid;
}

async function loadProduct(id, session, seller, basket) {
	const uri = Registry.apiUrl(`product/${id}`);
	const url = new window.URL(uri);
	url.searchParams.append('seller', seller);
	url.searchParams.append('session', session);
	url.searchParams.append('basket', basket);

	const config = {headers: {'Accept': 'application/json'}};
	const response = await fetch(url, config);
	const data = await response.json();
	return data;
}

/* Product */

product.subscribe(state => {
	if (Array.isArray(data.steps)) {
		const changed = setVisible() + setState() + setVariation() + setPrice();
		if (changed) {
			product.set(data);
			if (Helper.development())
				console.log(data);
		}
	}
});

product.load = async (id, session, seller) => {
	try {
		const basket = Basket.getIndex();
		const source = await loadProduct(id, session, seller, basket);
		const restored = !!source.config;
		if (restored) {
			const target = await loadProduct(id, '', seller, '');
			const merger = new Merger(source, target);
			data = merger.merged();
			data.warnings = merger.warnings();
		}
		else data = source;

		if (!data.config) {
			data.config = {};
			data.step = {};
			data.property = {};
			data.group = {};
			data.variation = {};
			data.price = {};
		}

		product.set(data);

		if (restored) {
			product.setStep(0, {force_validation: true});

			const different = source.variation.shop_group_product_id != data.variation.shop_group_product_id;
			if (different) {
				data = source;

				let message = 'De nieuwe configuratie kan niet toegepast worden. ';
				message += '<b>De oude configuratie wordt gebruikt.</b>';
				data.warnings = [{message}];

				product.set(data);
				product.setStep(0, {force_validation: true});
			}
		}

		if (Helper.development())
			console.log(data);
	}
	catch (error) {
		console.error(error);
	}
};

product.setStep = (order, config) => {
	if (!config) config = {};
	const marker = {step: false, property: false, group: false};
	const steps = data.steps;
	for (let step of steps) {
		step.active = step.order === order;
		if (step.active) {
			marker.step = true;
			for (let property of step.properties) {
				property.active = !marker.property && property.visible;
				if (property.active) {
					data.property = property;
					marker.property = true;
				}

				if (property.active && validGroups(property.groups)) {
					let first; for (let group of property.groups)
						if (validGroup(group)) {
							first = group;
							break;
						}

					if (first) for (let group of property.groups)
						if (validGroup(group)) {
							group.active = group == first;
							if (group.active) {
								data.group = group;
								marker.group = true;
							}
						}
				}
			}

			data.step = step;
		}
	}

	const valid = !order || (order && marker.step);
	if (valid) {
		for (let key in marker)
			if (!marker[key])
				data[key] = {};

		data.config.force_step_state = !!config.force_validation;
		data.steps = steps;
		product.set(data);
	}

	return valid;
};

product.setProperty = property => {
	const marker = {property: false, group: false};
	const properties = data.step.properties;
	for (let item of properties) {
		item.active = item == property;
		if (item.active) {
			data.property = item;
			marker.property = true;
		}

		if (item.active && validGroups(item.groups)) {
			let first; for (let group of item.groups)
				if (validGroup(group)) {
					first = group;
					break;
				}

			for (let group of item.groups) {
				group.active = group == first;
				if (group.active) {
					data.group = group;
					marker.group = true;
				}
			}
		}
	}

	for (let key in marker)
		if (!marker[key])
			data[key] = {};

	data.step.properties = properties;
	data.config.scroll = true;
	product.set(data);
};

product.setGroup = group => {
	let activated = false;
	const properties = data.step.properties;
	for (let property of properties)
		if (property.active && validGroups(property.groups))
			for (let item of property.groups)
				if (validGroup(item)) {
					item.active = item == group;
					if (item.active) {
						data.group = item;
						activated = true;
					}
				}

	if (!activated) data.group = {};
	data.step.properties = properties;
	product.set(data);
};

product.setPrevious = () => {
	let previous = false
	const properties = data.step.properties;
	for (let index = 0; index < properties.length; index++) {
		if (!properties[index].active && properties[index].visible && product.menuVisibleProperty(properties[index]))
			previous = properties[index];

		if (properties[index].active) {
			if (!previous) {
				let valid = data.step.order > 1;
				if (valid) valid = product.setStep(data.step.order - 1);
				if (!valid) {
					product.setStep(0, {force_validation: true});
					Layout.info();
				}
			}
			else product.setProperty(previous);
			break;
		}
	}
};

product.setNext = () => {
	let activated = false;
	const properties = data.step.properties;
	for (let index = 0; index < properties.length; index++) {
		const item = properties[index];
		if (item.active) {
			activated = true;
			let found = false;
			for (let next = index + 1; next < properties.length; next++) {
				if (properties[next].visible && product.menuVisibleProperty(properties[next])) {
					product.setProperty(properties[next]);
					found = true;
					break;
				}
			}

			if (!found) {
				const valid = product.setStep(data.step.order + 1);
				if (!valid) {
					product.setStep(0, {force_validation: true});
					Layout.quotation();
				}
			}

			break;
		}
	}

	if (!activated) {
		const valid = product.setStep(data.step.order + 1);
		if (!valid) {
			product.setStep(0, {force_validation: true});
			Layout.quotation();
		}
	}
};

product.visibleProperty = property => {
	const info = ['infos'].includes(property.property_type);
	const groups = validGroups(property.groups);
	const values = Helper.is(property.values);
	const items = Helper.is(property.bundle_items);
	let visible = info || groups || values || items;
	if (visible) {
		const restriction = property.visible_with_selected_upgrades;
		if (Helper.is(restriction)) {
			const selected = selectedIdValues();
			for (let id of restriction) {
				visible = selected.includes(id);
				if (visible) break;
			}
		}
	}

	return visible;
};

product.visibleGroup = (property, group) => {
	let visible = validGroup(group);
	if (visible) {
		const type = property.property_type;
		switch (type) {
			case 'upsells':
				const restriction = group.visible_with_selected_upgrades;
				visible = !Helper.is(restriction);
				if (Helper.is(restriction)) {
					const selected = selectedIdValues();
					for (let id of restriction) {
						visible = selected.includes(id);
						if (visible) break;
					}
				}
			break;
			case 'upgrades': visible = validGroups(property.groups) > 1; break;
		}
	}

	return visible;
};

product.menuVisibleProperty = (property, step) => {
	const type = property.property_type;
	const proceed = ['upsells', 'bundles'].includes(type);

	let visible = property.visible;
	if (visible && proceed) {
		visible = !!validGroups(property.groups);
		if (visible) switch (type) {
			case 'upsells':

				visible = false;
				for (let item of property.groups)
					if (validGroup(item))
						if (product.visibleGroup(property, item))
							{ visible = true; break; }

			break; case 'bundles':

				let first;
				let selected;
				const properties = step ? step.properties : data.step.properties;
				if (properties)
					for (let current of properties)
						if (current.group === property.group)
							if (current.visible) {
								if (!first) first = current;
								if (current.selected) selected = current;
							}

				if (first) {
					const is_first = property == first;
					const is_selected = property == selected;
					visible = selected ? is_selected : is_first;
				}

			break;
		}
	}

	return visible;
};

product.menuVisibleGroup = (property, group) => {
	const type = property.property_type;
	let visible; switch (type) {
		case 'upgrades': visible = validGroups(property.groups) > 1; break;
	}

	return visible;
}

product.price = value => {
	const config = {style: 'currency', currency: 'EUR'};
	const engine = new Intl.NumberFormat('nl-NL', config);
	const format = engine.format(value);
	const price = format.replace(/\,00$/, ',-');
	return price;
}

product.getPropertyName = property => {
	let name = '';
	switch (property.property_type) {
		case 'options': name = property.name; break;
		case 'infos': name = property.name || property.group; break;
		default: name = property.group || property.name; break;
	}

	return name;
};

product.variationPercentage = percentage => {
	const price = data?.variation?.price || 0;
	const valid = price && (percentage > 0);
	return valid ? (price * (percentage / 100)) : 0;
};

product.getValid = () => {
	let valid = true;
	for (let step of data.steps) {
		valid = step.state !== 'error';
		if (!valid) break;
	}

	return valid;
};

product.getURL = () => {
	let uri = '';
	for (let product of data.products)
		if (!Helper.undefined(product.url))
			if (product.id == data.id) {
				uri = product.url;
				break;
			}

	let url = '';
	if (uri) url = Registry.shopUrl(`configure/${uri}`);
	else url = window.location.href;
	return url;
};

export default product;